// src/pages/modals/ChatBox.jsx
import React, { useMemo, useEffect, useRef, useState, useCallback } from "react";
import axios from "../../api/axios";
import { FileText, X, ChevronDown, ChevronUp, Eye, Download } from "react-feather";
import ChatPortal from "./ChatPortal";
import "./ChatBox.css";

export default function ChatBox({
  isOpen, onClose,
  decisions = [],
  lang = "kk",
  onDownloadDecisionFile,
  currentUserId,
  canReview = false,
  alreadyReviewed = false,
  onSubmitReview = () => {},
  isScientist = false,
  allowUploadSend = false,
  continuationMode = "new",
  applicationId, application_id, appId,
  token,
  onRefreshDecisions = () => {},
  isInternalExpert = false,
  isDepartmentHead = false,
  isDeputyDirector = false,
}) {
  const appID  = applicationId ?? application_id ?? appId;
  const uiLang = lang === "kz" ? "kk" : lang;

  const T = {
    kk: {
      title: "Құжатты келісу парағы",
      subtitle: "Құжат нұсқалары және сарапшылар шешімі",
      uploadSend: "Жүктеп және жіберу",
      chooseFile: "Файл таңдаңыз (.docx)",
      sentNow: "✅ Жіберілді",
      needFile: "Файл таңдаңыз",
      scientist: "Ғалым", teacher: "Педагог",
      download: "Жүктеу", openDoc: "Құжатты ашу",
      noData: "Деректер жоқ", sentBy: "Оқушы жіберген құжат",
      loading: "Жүктелуде...",
      statusLabel: "Қаралуда",
      accept: "Қабылдаймын", reject: "Қайтарамын",
      hintView: "Тек қарау режимі.", hintDone: "Сіз жауап бердіңіз.",
      yourDecision: "Сіздің шешіміңіз",
      commentPlaceholder: "Пікір қалдыру...",
      conclusionLabel: "Шешім",
      acceptStat: "Қабылдады", rejectStat: "Қайтарды", waitStat: "Күтуде",
      progressTitle: (v) => `${v}-нұсқа бойынша шешім барысы`,
      progressSub: (a, t) => `${a} / ${t} сарапшы шешім қабылдады`,
      version: (n) => `${n}-нұсқа`,
      versionUploaded: (n) => `${n}-нұсқа жүктелді`,
      reviewerRejected: (name) => `${name} қайтарды`,
      studentResubmit: "Оқушы түзетілген құжатты қайта жүктеді",
      currentReview: (v) => `${v}-нұсқа қаралуда`,
      currentPeriod: "Қазіргі кезең",
      opinion: "Пікір",
      noReviews: "Рецензиялар күтілуде...",
      rejected: "Қайтарылды",
      reviewing: "Қаралуда",
      noFile: "Файл жоқ",
    },
    ru: {
      title: "Лист согласования документа",
      subtitle: "Версии документа и решения экспертов",
      uploadSend: "Загрузить и отправить",
      chooseFile: "Выберите файл (.docx)",
      sentNow: "✅ Отправлено",
      needFile: "Выберите файл",
      scientist: "Ученый", teacher: "Педагог",
      download: "Скачать", openDoc: "Открыть документ",
      noData: "Нет данных", sentBy: "Документ от учащегося",
      loading: "Загрузка...",
      statusLabel: "На рассмотрении",
      accept: "Принимаю", reject: "Отклоняю",
      hintView: "Только просмотр.", hintDone: "Вы уже ответили.",
      yourDecision: "Ваше решение",
      commentPlaceholder: "Оставить комментарий...",
      conclusionLabel: "Заключение",
      acceptStat: "Принято", rejectStat: "Отклонено", waitStat: "Ожидание",
      progressTitle: (v) => `Ход решения по версии ${v}`,
      progressSub: (a, t) => `${a} / ${t} эксперт вынес решение`,
      version: (n) => `Версия ${n}`,
      versionUploaded: (n) => `Версия ${n} загружена`,
      reviewerRejected: (name) => `${name} отклонил`,
      studentResubmit: "Учащийся загрузил исправленный документ",
      currentReview: (v) => `Версия ${v} на рассмотрении`,
      currentPeriod: "Текущий этап",
      opinion: "Отзыв",
      noReviews: "Рецензии ожидаются...",
      rejected: "Отклонено",
      reviewing: "На рассмотрении",
      noFile: "Файл не загружен",
    },
  }[uiLang];

  const [isLight,      setIsLight]      = useState(false);
  const [dragOver,     setDragOver]     = useState(false);
  const [comment,      setComment]      = useState("");
  const [sending,      setSending]      = useState(false);
  const [downloadingId,setDownloadingId]= useState(null);
  const [upFile,       setUpFile]       = useState(null);
  const [upComment,    setUpComment]    = useState("");
  const [upConclusion, setUpConclusion] = useState("");
  const [uploading,    setUploading]    = useState(false);
  const [scientistConclusion, setScientistConclusion] = useState("");
  const [reviewedLocal,setReviewedLocal]= useState(false);
  const [loadingFeed,  setLoadingFeed]  = useState(false);
  const [expandedOld,  setExpandedOld]  = useState({}); // id -> bool
  const [decisionsLocal, setDecisionsLocal] = useState(
    Array.isArray(decisions) ? decisions : []
  );

  useEffect(() => {
    if (Array.isArray(decisions) && decisions.length > 0) setDecisionsLocal(decisions);
  }, [decisions]);

  const loadDecisions = useCallback(async () => {
    if (!appID) return;
    setLoadingFeed(true);
    try {
      const { data } = await axios.get(
        `/external-expert/scientist-decisions/by-application/${appID}/full`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = Array.isArray(data?.data) ? data.data : [];
      setDecisionsLocal(list);
      setScientistConclusion(data?.scientist_conclusion || "");
    } catch (e) { console.error("chat load error", e); setDecisionsLocal([]); }
    finally { setLoadingFeed(false); }
  }, [appID, token]);

  useEffect(() => { if (isOpen && appID) loadDecisions(); }, [isOpen, appID, loadDecisions]);

  const latestMyDecision = useMemo(() => {
    const mine = (decisionsLocal || []).map(d => d?.decision).filter(Boolean).filter(s => s.is_my_decision === true);
    mine.sort((a, b) => (Number(a.id)||0) - (Number(b.id)||0));
    return mine[mine.length - 1] || null;
  }, [decisionsLocal]);

  const latestSent = useMemo(() => {
    const sent = (decisionsLocal || []).filter(d => {
      const s = String(d?.decision?.status || "").toLowerCase().trim();
      return s === "жіберілді" || s === "қабылданды";
    });
    sent.sort((a,b) => String(a.decision?.submitted_at||"").localeCompare(String(b.decision?.submitted_at||"")));
    return sent[sent.length - 1] || null;
  }, [decisionsLocal]);

  const iReviewed = useMemo(() => {
    return (latestSent?.reviews || []).some(r =>
      (r.is_my_review === true || String(r.is_my_review) === "1" ||
       (currentUserId != null && String(r.reviewer_id) === String(currentUserId))) &&
      (r.decision || (r.comment && String(r.comment).trim()))
    );
  }, [latestSent, currentUserId]);

  // Отсортированные по дате (старые → новые)
  const sortedDecisions = useMemo(() => {
    return [...(decisionsLocal || [])].sort((a, b) =>
      String(a.decision?.submitted_at||"").localeCompare(String(b.decision?.submitted_at||""))
    );
  }, [decisionsLocal]);

  const latestDecision = sortedDecisions[sortedDecisions.length - 1];
  const latestSci      = latestDecision?.decision;

  // Статистика по последней версии
  const latestReviews = useMemo(() => {
    return (latestDecision?.reviews || []);
  }, [latestDecision]);

  const acceptedCount = latestReviews.filter(r => {
    const d = String(r.decision||"").toLowerCase();
    return d.includes("қабыл") || d.includes("прин");
  }).length;
  const rejectedCount = latestReviews.filter(r => {
    const d = String(r.decision||"").toLowerCase();
    return d.includes("откл") || d.includes("бас");
  }).length;
  const totalReviewers = latestReviews.length;
  const waitCount      = Math.max(0, totalReviewers - acceptedCount - rejectedCount);
  const progressPct    = totalReviewers > 0
    ? Math.round(((acceptedCount + rejectedCount) / totalReviewers) * 100) : 0;

  // Шаги таймлайна
  const timelineSteps = useMemo(() => {
    const steps = [];
    sortedDecisions.forEach((d, i) => {
      const sci = d?.decision;
      if (!sci) return;
      const vNum = i + 1;
      // Шаг: загрузка версии
      steps.push({
        key: `upload-${sci.id}`,
        label: T.versionUploaded(vNum),
        date: sci.submitted_at || "",
        type: "upload",
        done: true,
        vNum,
      });
      // Шаг: если был отклонён — рецензент
      const rejectedReview = (d.reviews || []).find(r => {
        const rd = String(r.decision||"").toLowerCase();
        return rd.includes("откл") || rd.includes("бас");
      });
      const sciStatus = String(sci.status||"").toLowerCase();
      if (sciStatus === "отклонен" && rejectedReview) {
        const rName = rejectedReview.reviewer_name_display || rejectedReview.reviewer_name || T.teacher;
        steps.push({
          key: `reject-${sci.id}`,
          label: T.reviewerRejected(rName),
          date: rejectedReview.reviewed_at || "",
          type: "reject",
          done: true,
        });
      }
    });
    // Последний шаг: текущее рассмотрение
    if (latestSci) {
      const vNum = sortedDecisions.length;
      const sciStatus = String(latestSci.status||"").toLowerCase();
      if (sciStatus !== "отклонен") {
        steps.push({
          key: "current",
          label: T.currentReview(vNum),
          date: T.currentPeriod,
          type: "current",
          done: false,
        });
      }
    }
    return steps;
  }, [sortedDecisions, latestSci, T]);

  const scrollRef = useRef(null);

  const handleDownload = async (fileId) => {
    if (!fileId || downloadingId) return;
    try {
      setDownloadingId(fileId);
      if (typeof onDownloadDecisionFile === "function") {
        await onDownloadDecisionFile(fileId);
      } else {
        const res = await axios.get(
          `/external-expert/scientist-decisions/${fileId}/download`,
          { responseType: "blob", headers: { Authorization: `Bearer ${token}` } }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a   = document.createElement("a");
        a.href    = url;
        a.download= `scientist_decision_${fileId}.docx`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      }
    } finally { setDownloadingId(null); }
  };

  const handleUploadAndSend = async () => {
    if (!upFile) return alert(T.needFile);
    if (!appID) return;
    const isContinuation = continuationMode === "continuation";
    if (isContinuation && !upConclusion.trim())
      return alert(lang === "kk" ? "Шешім мәтінін жазыңыз" : "Введите текст решения");
    const fd = new FormData();
    fd.append("file", upFile); fd.append("application_id", appID);
    if (upComment?.trim()) fd.append("comment", upComment.trim());
    if (isContinuation) fd.append("scientist_conclusion", upConclusion.trim());
    try {
      setUploading(true);
      await axios.post("/external-expert/scientist-decisions/upload-send", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      alert(T.sentNow);
      setUpFile(null); setUpComment(""); setUpConclusion("");
      await loadDecisions(); onRefreshDecisions?.();
    } catch (err) {
      const msg = err?.response?.data?.message;
      if (msg) alert(msg); else alert("Ошибка загрузки/отправки");
    } finally { setUploading(false); }
  };

  useEffect(() => {
    if (!isOpen) return;
    const fn = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const canDepartmentHeadReview =
    isDepartmentHead && !isDeputyDirector && decisionsLocal.length > 0 && !iReviewed && !reviewedLocal;
  const canReviewerReview =
    !isScientist && !isDepartmentHead && !isDeputyDirector && !!latestSent && !iReviewed && !reviewedLocal;

  return (
    <ChatPortal>
      <div className="cbx-backdrop" onClick={onClose}>
        <div
          className={`cbx-modal${isLight ? " cbx-light" : ""}`}
          onClick={e => e.stopPropagation()}
        >
          {/* ── HEADER ── */}
          <div className="cbx-header">
            <div className="cbx-header__left">
              <div className="cbx-header__icon">📋</div>
              <div>
                <div className="cbx-header__title">{T.title}</div>
                <div className="cbx-header__sub">{T.subtitle}</div>
              </div>
            </div>
            <div className="cbx-header__right">
              <span className="cbx-status-badge">● {T.statusLabel}</span>
              <button className="cbx-theme-btn" onClick={() => setIsLight(v => !v)}
                title={isLight ? "Тёмная тема" : "Светлая тема"}>
                {isLight ? "🌙" : "☀️"}
              </button>
              <button className="cbx-close" onClick={onClose}><X size={16} /></button>
            </div>
          </div>



          {/* ── BODY ── */}
          <div className="cbx-body" ref={scrollRef}>
            {loadingFeed && <div className="cbx-empty">{T.loading}</div>}
            {!loadingFeed && sortedDecisions.length === 0 && (
              <div className="cbx-empty">{T.noData}</div>
            )}

            {sortedDecisions.map((d, i) => {
              const sci      = d?.decision;
              if (!sci) return null;
              const vNum     = i + 1;
              const isLatest = i === sortedDecisions.length - 1;
              const sciStatus= String(sci.status||"").toLowerCase().trim();
              const isRejected = sciStatus === "отклонен";
              const fileName = sci.file_path ? sci.file_path.split("/").pop() : T.noFile;
              const expanded = expandedOld[sci.id];

              // Рецензии этой версии
              const reviews = (d.reviews || [])
                .slice()
                .sort((a,b) => (a.reviewed_at||"").localeCompare(b.reviewed_at||""));

              // Первый отклонивший
              const firstReject = reviews.find(r => {
                const rd = String(r.decision||"").toLowerCase();
                return rd.includes("откл") || rd.includes("бас");
              });

              return (
                <div key={sci.id} className={`cbx-version-block${isLatest ? " latest" : " old"}`}>

                  {/* Карточка документа */}
                  <div className={`cbx-doc-card${isRejected ? " rejected" : isLatest ? " active" : ""}`}>
                    <div className="cbx-doc-icon">
                      <svg width="22" height="28" viewBox="0 0 22 28" fill="none">
                        <path d="M2 2h12l5 5v19a1 1 0 01-1 1H2a1 1 0 01-1-1V3a1 1 0 011-1z"
                          fill="#1a2a44" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
                        <path d="M13 2v6h6" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                        <text x="3" y="22" fontSize="5.5" fontWeight="800" fill="#ef4444">DOCX</text>
                      </svg>
                    </div>

                    <div className="cbx-doc-info">
                      <div className="cbx-doc-version-badge">{T.version(vNum)}</div>
                      <div className="cbx-doc-name">{fileName}</div>
                      <div className="cbx-doc-meta">
                        {sci.submitted_at && <span>📅 {sci.submitted_at}</span>}
                      </div>
                    </div>

                    <div className="cbx-doc-right">
                      {isRejected ? (
                        /* Старая — отклонённая: статус + аккордеон */
                        <div className="cbx-doc-right__col">
                          <span className="cbx-doc-status cbx-doc-status--rejected">
                            ↩ {T.rejected}
                          </span>
                          {firstReject?.comment && (
                            <button
                              className="cbx-doc-accordion"
                              onClick={() => setExpandedOld(p => ({ ...p, [sci.id]: !p[sci.id] }))}
                            >
                              <span>💬 {T.opinion}: {firstReject.comment}</span>
                              {expanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                            </button>
                          )}
                        </div>
                      ) : isLatest ? (
                        /* Последняя: кнопки */
                        <div className="cbx-doc-right__row">
                          <span className="cbx-doc-status cbx-doc-status--reviewing">
                            ● {T.reviewing}
                          </span>
                          {sci.file_path && (
                            <>
                              <button className="cbx-doc-btn cbx-doc-btn--outline"
                                onClick={() => handleDownload(sci.id)} disabled={!!downloadingId}>
                                <Eye size={13}/> {T.openDoc}
                              </button>
                              <button
                                className={`cbx-doc-btn cbx-doc-btn--primary${downloadingId===sci.id?" loading":""}`}
                                onClick={() => handleDownload(sci.id)} disabled={!!downloadingId}>
                                {downloadingId===sci.id
                                  ? <><span className="cbx-spinner"/>{lang==="kk"?"Жүктелуде...":"Загрузка..."}</>
                                  : <><Download size={13}/>{T.download}</>
                                }
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        /* Промежуточная одобренная */
                        <span className="cbx-doc-status cbx-doc-status--done">✓</span>
                      )}
                    </div>
                  </div>

                  {isLatest && scientistConclusion && (
                    <div className="cbx-conclusion-banner">
                      <span className="cbx-conclusion-banner__label">
                        {uiLang === "kk" ? "Шешім" : "Заключение"}:
                      </span>
                      <span className="cbx-conclusion-banner__text">{scientistConclusion}</span>
                    </div>
                  )}

                  {/* Аккордеон: рецензии старой версии */}
                  {isRejected && expanded && reviews.length > 0 && (
                    <div className="cbx-accordion-reviews">
                      {reviews.map((r, ri) => {
                        const rName  = r.reviewer_name_display || r.reviewer_name || T.teacher;
                        const rd     = String(r.decision||"").toLowerCase();
                        const rClass = rd.includes("қабыл")||rd.includes("прин") ? "accept"
                                     : rd.includes("откл")||rd.includes("бас")   ? "reject" : "wait";
                        const rLabel = rClass==="accept"?T.acceptStat:rClass==="reject"?T.rejectStat:T.waitStat;
                        return (
                          <div key={ri} className="cbx-reviewer-row">
                            <div className="cbx-rev-top">
                              <div className={`cbx-num cbx-num--${rClass}`}>{ri+1}</div>
                              <div className="cbx-rev-avatar">{initials(rName)}</div>
                              <div className="cbx-rev-name">{rName}</div>
                              <span className={`cbx-rev-status cbx-rev-status--${rClass}`}>{rLabel}</span>
                              {r.reviewed_at && <span className="cbx-rev-time">⏱ {r.reviewed_at.slice(11,16)}</span>}
                            </div>
                            {r.comment && <div className="cbx-rev-bottom">{r.comment}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Для последней версии: прогресс + рецензии */}
                  {isLatest && (
                    <>
                      {/* Прогресс */}
                      <div className="cbx-progress-section">
                        <div className="cbx-progress-header">
                          <span className="cbx-progress-section__title">
                            {T.progressTitle(vNum)}
                          </span>
                          <div className="cbx-stats-inline">
                            <span className="cbx-stat cbx-stat--accept">
                              <span className="cbx-stat__dot"/>✓ {T.acceptStat}: {acceptedCount}
                            </span>
                            <span className="cbx-stat cbx-stat--reject">
                              <span className="cbx-stat__dot"/>↩ {T.rejectStat}: {rejectedCount}
                            </span>
                            <span className="cbx-stat cbx-stat--wait">
                              <span className="cbx-stat__dot"/>⏱ {T.waitStat}: {waitCount}
                            </span>
                          </div>
                        </div>
                        <div className="cbx-progress-section__sub">
                          {T.progressSub(acceptedCount+rejectedCount, totalReviewers)}
                        </div>
                        <div className="cbx-progress-bar">
                          <div className="cbx-progress-bar__fill" style={{width:`${progressPct}%`}}/>
                        </div>
                      </div>

                      {/* Рецензенты */}
                      {reviews.length === 0
                        ? <div className="cbx-no-reviews">{T.noReviews}</div>
                        : reviews.map((r, ri) => {
                            const rName  = r.reviewer_name_display || r.reviewer_name || T.teacher;
                            const rd     = String(r.decision||"").toLowerCase();
                            const rClass = rd.includes("қабыл")||rd.includes("прин") ? "accept"
                                         : rd.includes("откл")||rd.includes("бас")   ? "reject" : "wait";
                            const rLabel = rClass==="accept"?T.acceptStat:rClass==="reject"?T.rejectStat:T.waitStat;
                            return (
                              <div key={ri} className="cbx-reviewer-row">
                                <div className="cbx-rev-top">
                                  <div className={`cbx-num cbx-num--${rClass}`}>{ri+1}</div>
                                  <div className="cbx-rev-avatar">{initials(rName)}</div>
                                  <div className="cbx-rev-name">{rName}</div>
                                  <span className={`cbx-rev-status cbx-rev-status--${rClass}`}>{rLabel}</span>
                                  {r.reviewed_at
                                    ? <span className="cbx-rev-time">⏱ {r.reviewed_at.slice(11,16)}</span>
                                    : <span className="cbx-rev-time">—</span>
                                  }
                                  {r.comment && <span className="cbx-rev-comment-short">💬 {r.comment}</span>}
                                </div>
                              </div>
                            );
                          })
                      }
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── FOOTER ── */}
          <div className="cbx-footer">

            {/* Блок учёного */}
            {allowUploadSend && isScientist && (() => {
              const status    = String(latestMyDecision?.status||"").toLowerCase().trim();
              const canResend = !latestMyDecision || status === "отклонен";
              const isCont    = continuationMode === "continuation";
              return canResend ? (
                <div className={`cbx-uploadsend${uploading?" is-uploading":""}`}>
                  <div className="cbx-file-row">
                    <label className={`cbx-file-label${dragOver?" dragover":""}`}
                      onDragEnter={()=>setDragOver(true)}
                      onDragOver={e=>{e.preventDefault();setDragOver(true);}}
                      onDragLeave={()=>setDragOver(false)}
                      onDrop={e=>{e.preventDefault();setDragOver(false);
                        const f=e.dataTransfer.files?.[0];
                        if(!f)return;
                        if(!f.name.toLowerCase().endsWith(".docx")){alert("❌ .docx");return;}
                        setUpFile(f);
                      }}>
                      📎 {upFile ? upFile.name : T.chooseFile}
                      <input type="file" accept=".docx" style={{display:"none"}}
                        onChange={e=>{const f=e.target.files?.[0];
                          if(!f)return;
                          if(!f.name.toLowerCase().endsWith(".docx")){alert("❌ .docx");e.target.value="";return;}
                          setUpFile(f);
                        }}/>
                    </label>
                    {upFile && <button className="cbx-file-clear" onClick={()=>setUpFile(null)}>✕</button>}
                  </div>
                  {uploading && <div className="cbx-progress-linear"><div className="cbx-progress-linear__bar"/></div>}
                  {isCont && (
                    <div className="cbx-conclusion-input">
                      <div className="cbx-conclusion-input__label">
                        {lang==="kk"?"Шешім * (міндетті)":"Решение * (обязательно)"}
                      </div>
                      <textarea className="cbx-textarea" rows={3}
                        placeholder={lang==="kk"?"Шешімді жазыңыз...":"Введите текст решения..."}
                        value={upConclusion} onChange={e=>setUpConclusion(e.target.value)} disabled={uploading}/>
                    </div>
                  )}
                  <textarea className="cbx-textarea" rows={2}
                    placeholder={lang==="kk"?"Түсіндірме (қалауыңызша)":"Комментарий (необязательно)"}
                    value={upComment} onChange={e=>setUpComment(e.target.value)} disabled={uploading}/>
                  <button className={`cbx-upload-btn${uploading?" loading":""}`} disabled={uploading} onClick={handleUploadAndSend}>
                    {uploading?<><span className="cbx-spinner"/>{lang==="kk"?"Жүктелуде…":"Загрузка…"}</>:<>📤 {T.uploadSend}</>}
                  </button>
                </div>
              ) : null;
            })()}

            {/* Блок рецензента / руководителя */}
            {(canDepartmentHeadReview || canReviewerReview) ? (
              <div className="cbx-composer">
                <div className="cbx-composer__title">{T.yourDecision}</div>
                <textarea className="cbx-textarea" rows={2}
                  placeholder={T.commentPlaceholder}
                  value={comment} onChange={e=>setComment(e.target.value)}/>
                <div className="cbx-actions">
                  <button className="cbx-btn cbx-btn--accept" disabled={sending}
                    onClick={async () => {
  try {
    setSending(true);
    const activeDecision = sortedDecisions[sortedDecisions.length - 1]; // ✅
    const reviewId = canDepartmentHeadReview ? activeDecision?.decision?.id : undefined;
    await onSubmitReview("қабылдады", comment, reviewId);
    setComment(""); setReviewedLocal(true); await loadDecisions();
  } finally { setSending(false); }
}}>
                    {sending?<span className="cbx-spinner"/>:"✓"} {T.accept}
                  </button>
                  <button className="cbx-btn cbx-btn--reject" disabled={sending}
                    onClick={async () => {
  try {
    setSending(true);
    const activeDecision = sortedDecisions[sortedDecisions.length - 1]; // ✅
    const reviewId = canDepartmentHeadReview ? activeDecision?.decision?.id : undefined;
    await onSubmitReview("отклонил", comment, reviewId);
    setComment(""); setReviewedLocal(true); await loadDecisions();
  } finally { setSending(false); }
}}>
                    ↩ {T.reject}
                  </button>
                </div>
              </div>
            ) : (
              <div className="cbx-tip">
                {alreadyReviewed||reviewedLocal||iReviewed ? T.hintDone : T.hintView}
              </div>
            )}

          </div>
        </div>
      </div>
    </ChatPortal>
  );
}

function initials(name=""){
  return name.trim().split(/\s+/).slice(0,2).map(p=>p[0]?.toUpperCase()||"").join("");
}